import { useRef, useState } from 'react';
import { Polygon, Tooltip, useMap, useMapEvents } from 'react-leaflet';
import { useSelector, useDispatch } from 'react-redux';
import { LeafletMouseEvent } from 'leaflet';
import { RootState } from '../../store/store';
import { addShape } from '../../store/slices/missionPlannerSlice';
import { calculateDistance } from '../../utils/coordinates';
import { shapeVertices, shapeAreaM2, formatArea } from '../../utils/geometry';
import { PlannerShape } from '../../types/missionPlanner';

const MIN_RADIUS_M = 10;

interface DrawState {
  center: { lat: number; lng: number };
  radiusM: number;
}

/**
 * Click-and-drag on the map to draw the active shape: mousedown sets the
 * centre, drag grows the radius (with a live area label), mouseup commits it.
 * Map panning is suspended during the draw so the drag sizes the shape.
 */
export const ShapeDrawHandler: React.FC = () => {
  const dispatch = useDispatch();
  const tool = useSelector(
    (state: RootState) => state.missionPlanner.activeShapeTool
  );
  const map = useMap();
  const [draw, setDraw] = useState<DrawState | null>(null);
  const centerRef = useRef<{ lat: number; lng: number } | null>(null);

  const radiusFrom = (center: { lat: number; lng: number }, e: LeafletMouseEvent) =>
    calculateDistance(center.lat, center.lng, e.latlng.lat, e.latlng.lng) * 1000;

  useMapEvents({
    mousedown(e) {
      if (!tool) return;
      map.dragging.disable();
      const center = { lat: e.latlng.lat, lng: e.latlng.lng };
      centerRef.current = center;
      setDraw({ center, radiusM: 0 });
    },
    mousemove(e) {
      const center = centerRef.current;
      if (!tool || !center) return;
      setDraw({ center, radiusM: radiusFrom(center, e) });
    },
    mouseup(e) {
      const center = centerRef.current;
      if (!tool || !center) return;
      map.dragging.enable();
      centerRef.current = null;
      setDraw(null);
      const radiusM = radiusFrom(center, e);
      if (radiusM >= MIN_RADIUS_M) {
        dispatch(
          addShape({ kind: tool, center, radiusM, radiusMinorM: radiusM, rotationDeg: 0 })
        );
      }
    },
  });

  if (!tool || !draw || draw.radiusM < MIN_RADIUS_M) {
    return null;
  }

  const preview: PlannerShape = {
    id: 'draw-preview',
    kind: tool,
    center: draw.center,
    radiusM: draw.radiusM,
    radiusMinorM: draw.radiusM,
    rotationDeg: 0,
  };

  return (
    <Polygon
      positions={shapeVertices(preview)}
      color="#f57f17"
      weight={2}
      dashArray="6, 6"
      fillOpacity={0.1}
      className="planner-shape-preview"
    >
      <Tooltip permanent direction="center" className="planner-shape-area">
        {formatArea(shapeAreaM2(preview))}
      </Tooltip>
    </Polygon>
  );
};
